import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccount } from '@/api/useAccount';
import { isAuthConfigured } from '@/auth/config';
import { Permissions } from '@/auth/permissions';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  boardHref,
  groupDashboard,
  type DashboardSection,
  type TechnicianGroup,
} from '@/lib/dashboardGroups';
import { formatDate, formatDateOnly, formatTime } from '@/lib/format';
import {
  DashboardTimezoneError,
  useOpenVisits,
  type DashboardVisit,
} from '@/routes/dashboard/useOpenVisits';

/**
 * Where `/` takes you after signing in (M2-17, Kyle 2026-10-02):
 * - the office (`jobs.read.all`: Owner, Admin, Dispatcher) gets the dashboard;
 * - everyone else goes to `/jobs`. For a Technician that is their own job
 *   list, which the API narrows to their work (M1-22); a real "my day" screen
 *   is a later story (Q3 = A).
 *
 * ⚠️ There is deliberately no third branch. Someone with neither permission,
 * or whose `/me` failed, is ALSO sent to `/jobs`, and that route's own
 * `RequirePermission` shows them `Forbidden`. The first draft rendered
 * `Forbidden` here for those cases; mutation testing showed both branches
 * changed nothing anyone could see (W23/W24 in web/CONVENTIONS.md), so the
 * route's guard is the one place that answer lives.
 *
 * Decided on `/api/v1/me`'s permissions, never the role (web/CONVENTIONS.md:
 * the SPA does not reimplement the matrix). Inert without auth configured,
 * like every guard here, so a local checkout opens the dashboard.
 */
export function Home() {
  const { t } = useTranslation();
  const { data, isPending } = useCurrentUser();

  if (!isAuthConfigured) {
    return <Dashboard />;
  }

  if (isPending) {
    return (
      <p className="text-sm text-[var(--color-muted-foreground)]" role="status">
        {t('requirePermission.checkingAccess')}
      </p>
    );
  }

  if (data?.permissions?.includes(Permissions.readAllJobs)) {
    return <Dashboard />;
  }

  return <Navigate to="/jobs" replace />;
}

/** "N jobs waiting to be scheduled" goes to the board's unassigned rail (`UnassignedRail`'s anchor). */
export const UNSCHEDULED_RAIL_HREF = '/dispatch#unassigned-rail';

/**
 * The owner dashboard (M2-17): open visits that are late, today, or in the
 * next seven days, by day and technician, on the account's clock.
 *
 * Every date and flag comes from the API (`DashboardEndpoints`). The screen
 * groups the rows and prints times on the account's zone, the same clock the
 * server used for the days. Nothing here reads the viewer's own calendar.
 *
 * The tile row leaves room for OD-03's revenue and A/R tiles (M2-18), which
 * this story does not build.
 */
export function Dashboard() {
  const { t } = useTranslation();
  const openVisits = useOpenVisits();
  const account = useAccount();
  const timeZone = account.data?.timezone;

  const sections = useMemo(
    () => (openVisits.data ? groupDashboard(openVisits.data) : []),
    [openVisits.data],
  );

  // An error only replaces the screen when there is nothing to show. The
  // minute refetch (and focus) keeps going after a failure, and a blip on one
  // of them must not swap an office screen's visits for "couldn't be loaded"
  // until the next one lands (found in review, M2-17).
  const isError =
    (openVisits.isError && !openVisits.data) || (account.isError && !account.data);
  const isPending = !isError && (openVisits.isPending || account.isPending);

  return (
    <section className="space-y-6" aria-labelledby="dashboard-heading">
      <div>
        <h1 id="dashboard-heading" className="text-2xl font-semibold tracking-tight">
          {t('nav.dashboard')}
        </h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('dashboard.subtitle')}</p>
      </div>

      {isError ? (
        openVisits.error instanceof DashboardTimezoneError ? (
          <ErrorState title={t('dashboard.timezoneTitle')} description={openVisits.error.message} />
        ) : (
          <ErrorState
            title={t('dashboard.loadFailed')}
            onRetry={() => {
              void openVisits.refetch();
              void account.refetch();
            }}
          />
        )
      ) : isPending || !openVisits.data || !timeZone ? (
        <LoadingState label={t('dashboard.loading')} />
      ) : (
        <>
          <Tiles
            openJobs={Number(openVisits.data.openJobCount)}
            lateJobs={sections.find((s) => s.kind === 'late')?.jobCount ?? 0}
            unscheduledJobs={Number(openVisits.data.unscheduledJobCount)}
          />

          {Number(openVisits.data.olderLateJobCount) > 0 ? (
            <p className="text-sm" data-testid="older-late">
              <Link className="tap-target underline" to="/jobs">
                {t('dashboard.olderLate', { count: Number(openVisits.data.olderLateJobCount) })}
              </Link>
            </p>
          ) : null}

          {sections.length === 0 ? (
            <EmptyState
              title={t('dashboard.emptyTitle')}
              description={t('dashboard.emptyDescription')}
            />
          ) : (
            sections.map((section) => (
              <Section key={section.day ?? 'late'} section={section} timeZone={timeZone} />
            ))
          )}
        </>
      )}
    </section>
  );
}

function Tiles({
  openJobs,
  lateJobs,
  unscheduledJobs,
}: {
  openJobs: number;
  lateJobs: number;
  unscheduledJobs: number;
}) {
  const { t } = useTranslation();
  // Three across even on a phone: stacked, the tiles pushed the first visit
  // below the fold at 375px (browser check, M2-17).
  const tile = 'flex flex-col gap-1 rounded-lg border p-3 sm:p-4';

  return (
    <ul className="grid grid-cols-3 gap-2 sm:gap-3" aria-label={t('dashboard.summary')}>
      <li className={tile} data-testid="tile-open">
        <span className="text-2xl font-semibold">{openJobs}</span>{' '}
        <span className="text-xs text-[var(--color-muted-foreground)] sm:text-sm">
          {t('dashboard.openJobs', { count: openJobs })}
        </span>
      </li>
      <li className={tile} data-testid="tile-late">
        <span
          className={
            lateJobs > 0
              ? 'text-2xl font-semibold text-[var(--color-destructive)]'
              : 'text-2xl font-semibold'
          }
        >
          {lateJobs}
        </span>{' '}
        <span className="text-xs text-[var(--color-muted-foreground)] sm:text-sm">
          {t('dashboard.lateJobs', { count: lateJobs })}
        </span>
      </li>
      <li className={tile} data-testid="tile-unscheduled">
        <Link className="tap-row flex flex-col gap-1 hover:underline" to={UNSCHEDULED_RAIL_HREF}>
          <span className="text-2xl font-semibold">{unscheduledJobs}</span>{' '}
          <span className="text-xs text-[var(--color-muted-foreground)] sm:text-sm">
            {t('dashboard.waitingToBeScheduled', { count: unscheduledJobs })}
          </span>
        </Link>
      </li>
    </ul>
  );
}

/** A long weekday and date for a bare `YYYY-MM-DD` the server computed, read on UTC so it names that same day. */
function dayHeading(day: string): string {
  return formatDate(day, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

function Section({ section, timeZone }: { section: DashboardSection; timeZone: string }) {
  const { t } = useTranslation();
  const headingId = `dashboard-section-${section.day ?? 'late'}`;

  const title =
    section.kind === 'late'
      ? t('dashboard.late')
      : section.isToday
        ? t('dashboard.todayHeading', { date: dayHeading(section.day!) })
        : dayHeading(section.day!);

  return (
    <section
      aria-labelledby={headingId}
      className="space-y-3"
      data-testid={`section-${section.day ?? 'late'}`}
    >
      <h2 id={headingId} className="flex flex-wrap items-baseline gap-x-3 text-lg font-semibold">
        {section.day ? (
          <Link className="tap-target hover:underline" to={boardHref(section.day)}>
            {title}
          </Link>
        ) : (
          <span className="text-[var(--color-destructive)]">{title}</span>
        )}
        <span className="text-sm font-normal text-[var(--color-muted-foreground)]">
          {t('dashboard.jobCount', { count: section.jobCount })}
        </span>
      </h2>

      {section.groups.map((group) => (
        <Group
          key={group.technicianId ?? 'unassigned'}
          section={section}
          group={group}
          timeZone={timeZone}
        />
      ))}
    </section>
  );
}

function Group({
  section,
  group,
  timeZone,
}: {
  section: DashboardSection;
  group: TechnicianGroup;
  timeZone: string;
}) {
  const { t } = useTranslation();
  const name = group.technicianName ?? t('dashboard.unassigned');

  return (
    // A plain block, not a <section>: a technician appears under several
    // days, and a region per group would be landmarks with the same name
    // (axe's landmark-unique). The <h3> carries the structure.
    <div className="rounded-lg border" data-testid="technician-group">
      <h3 className="flex flex-wrap items-baseline gap-x-2 border-b px-3 py-2 text-sm font-medium">
        {section.day ? (
          <Link
            className="tap-target hover:underline"
            to={boardHref(section.day, group.technicianId)}
          >
            {name}
          </Link>
        ) : (
          <span>{name}</span>
        )}
        <span className="font-normal text-[var(--color-muted-foreground)]">
          {t('dashboard.jobCount', { count: group.jobCount })}
        </span>
      </h3>
      <ul className="divide-y">
        {group.visits.map((visit) => (
          <VisitRow
            key={visit.visitId}
            visit={visit}
            timeZone={timeZone}
            showDate={section.kind === 'late'}
          />
        ))}
      </ul>
    </div>
  );
}

function VisitRow({
  visit,
  timeZone,
  showDate,
}: {
  visit: DashboardVisit;
  timeZone: string;
  showDate: boolean;
}) {
  const { t } = useTranslation();
  const start = formatTime(visit.scheduledStart, timeZone);
  const window = visit.scheduledEnd
    ? `${start} – ${formatTime(visit.scheduledEnd, timeZone)}`
    : start;
  const visitCount = Number(visit.visitCount);

  return (
    <li
      className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-start sm:gap-4"
      data-testid="dashboard-visit"
    >
      <div className="shrink-0 text-sm tabular-nums sm:w-40">
        {showDate ? <div className="font-medium">{formatDateOnly(visit.day)}</div> : null}
        <div>{window}</div>
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <Link className="tap-target font-medium hover:underline" to={`/jobs/${visit.jobId}`}>
            {visit.customerName}
          </Link>
          {visit.late ? <Flag kind="late" /> : null}
          {visit.notStarted ? <Flag kind="notStarted" /> : null}
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {[visit.propertyLine1, visit.propertyUnit].filter(Boolean).join(', ')}
        </p>
        <p className="text-sm">
          {visit.description}
          {visitCount > 1 ? (
            <span className="text-[var(--color-muted-foreground)]">
              {' · '}
              {t('dashboard.visitOf', { number: Number(visit.visitNumber), count: visitCount })}
            </span>
          ) : null}
          <span className="text-[var(--color-muted-foreground)]">
            {' · '}
            {t(`common.jobStatus.${visit.jobStatus}`, { defaultValue: visit.jobStatus })}
          </span>
        </p>
      </div>
      <Link
        className="tap-target shrink-0 text-sm underline"
        to={boardHref(visit.day, visit.technicianId)}
        aria-label={t('dashboard.onTheBoardFor', { customer: visit.customerName })}
      >
        {t('dashboard.onTheBoard')}
      </Link>
    </li>
  );
}

/**
 * Late and not-started say so in WORDS, beside a decorative glyph
 * (web/CONVENTIONS.md: status is never colour-only). Colour is a second cue,
 * from the already-checked destructive and foreground tokens, with no new
 * palette entry.
 */
function Flag({ kind }: { kind: 'late' | 'notStarted' }) {
  const { t } = useTranslation();
  return kind === 'late' ? (
    <span
      className="inline-flex items-center gap-1 rounded border border-[var(--color-destructive)] px-1.5 text-xs font-medium text-[var(--color-destructive)]"
      data-testid="flag-late"
    >
      <span aria-hidden="true">⚠</span>
      {t('dashboard.flagLate')}
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded border px-1.5 text-xs font-medium"
      data-testid="flag-not-started"
    >
      <span aria-hidden="true">◷</span>
      {t('dashboard.flagNotStarted')}
    </span>
  );
}
